import L, {
  type BaseIconOptions,
  type Map,
  type Layer,
  geoJSON,
  LatLng,
  Icon,
  Marker,
} from 'leaflet'
import { useEffect, useState } from 'react'
import { useNavigate, type NavigateFunction } from 'react-router'
import Supercluster, { PointFeature, AnyProps } from 'supercluster'
import { Geometry, Polygon, Point } from 'geojson'
import useBuildQueryString from '../../../../../shared/hooks/useBuildQueryString'
import { DETAIL_ICON } from '../../../config'
import { useMapContext } from '../../../../../shared/contexts/map/MapContext'
import { polygonParam, datasetParam } from '../../../query-params'
import type { BuildQueryString } from '../../../../../shared/hooks/useBuildQueryString/useBuildQueryString'
import useDrawResultsQuery from '../useDrawResultsQuery'
import { rdToWgs84 } from '../../../../../shared/utils/coordinateReferenceSystem'
import useParam from '../../../../../shared/hooks/useParam'
import { getDrawToolConfig, DrawToolResults } from '../config'
import { DatasetType } from '../../../../../shared/components/DataSelection/types'
import { getScopes } from '../../../../../shared/utils/auth/auth'
import isUserAuthorized from '../../../../../shared/utils/brk/userAuthorized'
import AuthScope from '../../../../../shared/utils/api/authScope'

export const CLUSTER_ICON_CLASS = 'cluster-icon'
export const SELECTION_DETAIL_CLASS = 'dataselection-detail-marker'

const getPointLayer = (point: Point) => {
  const [x, y] = point.coordinates
  const { latitude, longitude } = rdToWgs84({ x, y })

  return new Marker(new LatLng(latitude, longitude), {
    icon: new Icon({
      ...DETAIL_ICON,
      className: SELECTION_DETAIL_CLASS,
    } as BaseIconOptions),
  })
}

const convertPolygonCoordinates = (polygon: Polygon) => {
  return {
    ...polygon,
    coordinates: polygon.coordinates.map((coordinate) =>
      coordinate.map(([x, y]) => {
        const { latitude, longitude } = rdToWgs84({ x, y })

        return [longitude, latitude]
      }),
    ),
  }
}

const getPolygonLayer = (polygon: Polygon) => {
  const [layer] = geoJSON(convertPolygonCoordinates(polygon)).getLayers() as L.Polygon[]

  return layer
}

const getMarkerLayer = (geometry?: Geometry) => {
  switch (geometry?.type) {
    case 'Point':
      return getPointLayer(geometry)
    case 'Polygon':
      return getPolygonLayer(geometry)
    default:
      throw new Error(`Cannot handle ${geometry?.type}`)
  }
}

const getLayers = (
  visualizationData: DrawToolResults,
  navigate: NavigateFunction,
  buildQueryString: BuildQueryString,
  datasetType: DatasetType,
) =>
  visualizationData.results.reduce<[L.Marker[], L.Polygon[]]>(
    ([[...markers], [...polygons]], { identificatie, geometry }) => {
      const layer = getMarkerLayer(geometry)
      if (geometry.type === 'Point') {
        markers.push(layer as L.Marker)
      }
      if (geometry.type === 'Polygon') {
        polygons.push(layer as L.Polygon)
      }

      layer.on('click', (e) => {
        L.DomEvent.stopPropagation(e)
        const pathname = getDrawToolConfig(datasetType).getDetailPath(identificatie)
        const queryString = buildQueryString(undefined, [polygonParam])
        navigate(`${pathname}?${queryString}`)
      })

      return [markers, polygons]
    },
    [[], []],
  )

const getClusterIndex = (layers: L.Marker[]) => {
  const points = layers
    .filter((layer) => layer)
    .map((marker, index) => {
      const latLng = marker.getLatLng()

      return {
        type: 'Feature',
        properties: { id: index },
        geometry: {
          type: 'Point',
          coordinates: [latLng.lng, latLng.lat],
        },
      } as PointFeature<AnyProps>
    })

  const clusterIndex = new Supercluster({
    log: false,
    radius: 30,
    extent: 3000,
    maxZoom: 13,
  })
  clusterIndex.load(points)

  return clusterIndex
}

const getClustersOrMarkers = (map: Map, clusterIndex: Supercluster, layers: Layer[]) => {
  const bounds = map.getBounds()

  const clusters = clusterIndex.getClusters(
    [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
    map.getZoom(),
  )

  return clusters.map((clusterOrMarker) => {
    const [longitude, latitude] = clusterOrMarker.geometry.coordinates
    const { id, cluster, point_count: pointCount } = clusterOrMarker.properties

    if (cluster) {
      const clusterIcon = L.divIcon({
        html: `<div>${pointCount as number}</div>`,
        className: CLUSTER_ICON_CLASS,
        iconSize: L.point(40, 40),
      })

      const clusterMarker = L.marker([latitude, longitude], { icon: clusterIcon })

      clusterMarker.on('click', () => {
        map.setView([latitude, longitude], map.getZoom() + 1)
      })

      return clusterMarker
    }

    return layers[id]
  })
}

const useMapVisualizations = (map: Map) => {
  const [datasetType] = useParam(datasetParam)
  const maxMarkers = getDrawToolConfig(datasetType).maxNumberOfClusters
  const { showMapDrawVisualization } = useMapContext()
  const { buildQueryString } = useBuildQueryString()
  const navigate = useNavigate()
  const [visualizations, setVisualizations] = useState<Layer[]>([])
  const userScopes = getScopes()
  const { authScope } = getDrawToolConfig(datasetType)
  const authorized = isUserAuthorized(userScopes, authScope === AuthScope.None ? [] : [authScope])

  const { data: visualizationData } = useDrawResultsQuery(authorized, maxMarkers, true)

  useEffect(() => {
    if (!visualizationData || !showMapDrawVisualization) {
      setVisualizations([])

      return () => undefined
    }
    const [markers, polygons] = getLayers(
      visualizationData,
      navigate,
      buildQueryString,
      datasetType,
    )
    const clusterIndex = getClusterIndex(markers)

    const updateClusters = () => {
      setVisualizations([...polygons, ...getClustersOrMarkers(map, clusterIndex, markers)])
    }

    updateClusters()
    map.on('moveend', updateClusters)

    return () => {
      map.off('moveend', updateClusters)
    }
  }, [map, showMapDrawVisualization, visualizationData])

  useEffect(() => {
    visualizations.forEach((drawing) => drawing.addTo(map))

    return () => visualizations.forEach((drawing) => drawing.remove())
  }, [visualizations])
}

export default useMapVisualizations
